/**
 * A quest on a canvas.
 *
 * The same idea as the conversation canvas next door, and for the same reason:
 * a quest that forks is a shape, and a shape has to be looked at. A list with
 * dotted lines between its rows is still a list, and reading one to work out
 * which ending a branch reaches is work nobody should have to do.
 *
 * Steps run down the page in the order the journal counts them, so the trunk of
 * the quest is a column and every branch is an arc leaving it and coming back
 * in wherever it leads. Nothing is dragged: where a step sits is decided by
 * which step it is, so adding one in the middle rearranges the picture rather
 * than leaving a hole in it.
 *
 * Nothing is edited here. Pressing a node says which one is being written, and
 * the panel beside the canvas does the writing.
 */

import { useEffect, useRef, useState } from 'react';

/**
 * How big a node is drawn, which the wires need to know to meet its edges.
 *
 * Held rather than measured: every step is the same card with the same three
 * things on it, so a fixed size keeps the wires meeting the boxes exactly and
 * keeps a quest of thirty steps from being laid out again on every keystroke.
 * The card is clipped to it, which is why the text is clamped to two lines.
 */
const WIDTH = 236;
const HEIGHT = 124;

/** How far apart the rows are, top to top. */
const ROW = 164;

/** Where the trunk sits, with room to its left for arcs that run backwards. */
const TRUNK = 150;

const ZOOM = { least: 0.4, most: 1.5 };

/** What the canvas is being asked about: the quest itself, a step, or the end. */
export type Chosen = { kind: 'quest' } | { kind: 'step'; step: number } | { kind: 'end' };

export interface CanvasStep {
  /** What the journal says at this stage. */
  text: string;
  /** One line about what finishes it, drawn under the text. */
  waits: string;
  /** Where it can go other than straight on, and whether anything decides it. */
  branches: readonly { step: number; conditional: boolean }[];
}

/** What the end of the quest amounts to, for the node that is the end of it. */
export interface CanvasEnding {
  /** What the completion notice says, or nothing if it has not been written. */
  text: string;
  /** One line counting what it hands over, drawn underneath. */
  waits: string;
}

export interface CanvasView {
  x: number;
  y: number;
  zoom: number;
}

function sameChoice(one: Chosen, other: Chosen): boolean {
  if (one.kind !== other.kind) return false;
  return one.kind !== 'step' || other.kind !== 'step' || one.step === other.step;
}

export function QuestCanvas({
  name,
  steps,
  ending,
  chosen,
  at,
  view,
  onView,
  onChoose,
  onAddAfter,
  onBranch,
  onMove,
  onRemove,
}: {
  /** The quest's name, drawn on the node the quest itself is. */
  name: string;
  steps: readonly CanvasStep[];
  /** What the end of it says and gives, for the node that is the end of it. */
  ending: CanvasEnding;
  /** Which node the panel beside this is writing. */
  chosen: Chosen;
  /** Which step the character is actually on, counted the way the journal does. */
  at: number;
  view: CanvasView;
  onView: (view: CanvasView) => void;
  onChoose: (chosen: Chosen) => void;
  /** Puts a new step in after this one. Nought means before the first. */
  onAddAfter: (step: number) => void;
  onBranch: (step: number) => void;
  onMove: (step: number, by: -1 | 1) => void;
  onRemove: (step: number) => void;
}): JSX.Element {
  const surface = useRef<HTMLDivElement | null>(null);
  const [panning, setPanning] = useState<{ x: number; y: number } | null>(null);

  /**
   * The view is put on the quest the first time one is opened.
   *
   * Without it a quest opens wherever the last one was left, which for a canvas
   * that lays itself out is always wrong.
   */
  const framed = useRef('');
  useEffect(() => {
    if (framed.current === name) return;
    framed.current = name;

    const box = surface.current?.getBoundingClientRect();
    onView({ zoom: 1, x: Math.max(20, ((box?.width ?? 600) - WIDTH) / 2 - TRUNK), y: 24 });
    // Only when the quest changes: this is where it opens, not where it stays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  /** Every node, in the order they are drawn, with where each one sits. */
  const rows: { key: string; choice: Chosen; y: number }[] = [
    { key: 'quest', choice: { kind: 'quest' }, y: 0 },
    ...steps.map((_, index) => ({
      key: `step-${index + 1}`,
      choice: { kind: 'step' as const, step: index + 1 },
      y: (index + 1) * ROW,
    })),
    { key: 'end', choice: { kind: 'end' as const }, y: (steps.length + 1) * ROW },
  ];

  const middleOf = (rank: number): number => rank * ROW + HEIGHT / 2;

  /** The plain way on: the quest into its first step, and each step into the next. */
  const trunk: string[] = [];
  for (let rank = 0; rank < rows.length - 1; rank++) {
    const top = rank * ROW + HEIGHT;
    const bottom = (rank + 1) * ROW;
    trunk.push(
      `M ${TRUNK + WIDTH / 2} ${top} C ${TRUNK + WIDTH / 2} ${top + 22}, ${TRUNK + WIDTH / 2} ${bottom - 22}, ${TRUNK + WIDTH / 2} ${bottom}`,
    );
  }

  /**
   * Where a branch leads, drawn as an arc out to one side and back in.
   *
   * Out to the right when it skips ahead and to the left when it turns back, so
   * two branches from the same step never lie on top of each other and a loop
   * is recognisable as a loop.
   */
  const arcs: { key: string; d: string; lit: boolean; label: string; x: number; y: number }[] = [];
  steps.forEach((step, index) => {
    const from = index + 1;

    step.branches.forEach((branch, which) => {
      const to = branch.step === 0 ? steps.length + 1 : branch.step;
      if (to < 0 || to > steps.length + 1) return;

      const back = to <= from;
      const side = back ? -1 : 1;
      const edge = back ? TRUNK : TRUNK + WIDTH;
      const startY = middleOf(from) + which * 12 - 6;
      const endY = middleOf(to);
      const reach = (back ? 60 : 70) + Math.abs(to - from) * 26 + which * 18;

      arcs.push({
        key: `${from}-${which}`,
        d: `M ${edge} ${startY} C ${edge + side * reach} ${startY}, ${edge + side * reach} ${endY}, ${edge} ${endY}`,
        lit: sameChoice(chosen, { kind: 'step', step: from }),
        label: branch.conditional ? 'if' : 'or',
        x: edge + side * (reach * 0.75),
        y: (startY + endY) / 2,
      });
    });
  });

  const height = rows.length * ROW + 80;

  return (
    <div
      className="graph quest-graph"
      ref={surface}
      onContextMenu={(event) => event.preventDefault()}
      onPointerDown={(event) => {
        if ((event.target as HTMLElement).closest('[data-node]')) return;

        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          // The gesture works without it; it just stops at the edge.
        }
        setPanning({ x: event.clientX, y: event.clientY });
      }}
      onPointerMove={(event) => {
        if (!panning) return;

        onView({
          ...view,
          x: view.x + (event.clientX - panning.x),
          y: view.y + (event.clientY - panning.y),
        });
        setPanning({ x: event.clientX, y: event.clientY });
      }}
      onPointerUp={() => setPanning(null)}
      onPointerCancel={() => setPanning(null)}
      onWheel={(event) => {
        // The wheel scrolls, the way it does everywhere else, and Shift and the
        // wheel zooms. A quest is a column that runs off the bottom of the
        // window long before it runs off the side, so reading one is scrolling
        // it, and zooming is the thing that has to be asked for.
        if (!event.shiftKey) {
          onView({ ...view, x: view.x - event.deltaX, y: view.y - event.deltaY });
          return;
        }

        // Held with Shift, some platforms report the turn as sideways. Either
        // reading means the same gesture, so whichever came through is taken.
        const turned = event.deltaY !== 0 ? event.deltaY : event.deltaX;
        if (turned === 0) return;

        const to = Math.min(ZOOM.most, Math.max(ZOOM.least, view.zoom * (turned > 0 ? 0.9 : 1.1)));

        const box = surface.current?.getBoundingClientRect();
        const px = event.clientX - (box?.left ?? 0);
        const py = event.clientY - (box?.top ?? 0);

        // Zoomed about the pointer, so whatever is being looked at stays put.
        onView({
          zoom: to,
          x: px - ((px - view.x) * to) / view.zoom,
          y: py - ((py - view.y) * to) / view.zoom,
        });
      }}
    >
      <div
        className="graph-world"
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})` }}
      >
        <svg className="graph-wires" overflow="visible" style={{ height }}>
          {trunk.map((d, index) => (
            <path key={`trunk-${index}`} className="wire" d={d} />
          ))}
          {arcs.map((arc) => (
            <path key={arc.key} className={arc.lit ? 'wire lit' : 'wire'} d={arc.d} />
          ))}
        </svg>

        {arcs.map((arc) => (
          <span
            key={`${arc.key}-label`}
            className="quest-arc-label"
            style={{ left: arc.x, top: arc.y }}
          >
            {arc.label}
          </span>
        ))}

        {rows.map((row) => {
          const lit = sameChoice(chosen, row.choice);
          const here =
            (row.choice.kind === 'quest' && at === 0) ||
            (row.choice.kind === 'step' && at === row.choice.step) ||
            (row.choice.kind === 'end' && at > steps.length && at > 0);

          const step = row.choice.kind === 'step' ? steps[row.choice.step - 1] : undefined;
          const number = row.choice.kind === 'step' ? row.choice.step : 0;

          return (
            <div
              key={row.key}
              data-node={row.key}
              className={`graph-node quest-node ${row.choice.kind}${lit ? ' on' : ''}${here ? ' here' : ''}`}
              style={{ left: TRUNK, top: row.y, width: WIDTH }}
              onPointerDown={(event) => {
                event.stopPropagation();
                onChoose(row.choice);
              }}
            >
              <div className="graph-node-head">
                <span className="graph-node-name">
                  {row.choice.kind === 'quest'
                    ? name || 'A new quest'
                    : row.choice.kind === 'end'
                      ? 'Finished'
                      : `Step ${number}`}
                </span>
                {here ? <span className="graph-badge">here</span> : null}
              </div>

              <div className="quest-node-body">
                <p className="quest-node-text">
                  {row.choice.kind === 'quest'
                    ? 'The quest itself: what it is called, what it says, and what it asks for.'
                    : row.choice.kind === 'end'
                      ? ending.text || 'Anything reaching here has finished it.'
                      : step?.text || 'Nothing written yet'}
                </p>

                {row.choice.kind === 'end' ? (
                  <p className="quest-node-waits">{ending.waits}</p>
                ) : step && step.waits ? (
                  <p className="quest-node-waits">{step.waits}</p>
                ) : null}
              </div>

              {/* The buttons that change the quest, on the node they change.
                  Everything that is not a step gets the one that starts one. */}
              <div className="quest-node-tools">
                {row.choice.kind === 'quest' ? (
                  <button
                    type="button"
                    title="Put a step at the beginning"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={() => onAddAfter(0)}
                  >
                    +
                  </button>
                ) : row.choice.kind === 'end' ? null : (
                  <>
                    <button
                      type="button"
                      title="Move this step earlier"
                      disabled={number <= 1}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={() => onMove(number, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      title="Move this step later"
                      disabled={number >= steps.length}
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={() => onMove(number, 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      title="Send this step somewhere other than the next one"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={() => onBranch(number)}
                    >
                      if
                    </button>
                    <button
                      type="button"
                      title="Put a step in after this one"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={() => onAddAfter(number)}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      className="danger"
                      title="Take this step out"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={() => onRemove(number)}
                    >
                      ✕
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
